import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, Paperclip, Trash2 } from "lucide-react";
import { crTime, crDateTime, chatDayLabel } from "@/lib/dateFormat";
import { toast } from "sonner";

/**
 * Private 1-to-1 chat between a teacher and a student. Independent from the
 * homework wall — used for direct communication (WhatsApp-style bubbles).
 */
export default function DirectChat({ peer, height = "min(65vh, 560px)" }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const refresh = async () => {
    if (!peer?.peer_id) return;
    try {
      const r = await api.get(`/dm/${peer.peer_id}`);
      setMessages(r.data || []);
      // Mark seen (fire and forget)
      api.post(`/dm/${peer.peer_id}/mark-seen`).catch(() => {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peer?.peer_id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t && !file) return;
    setPosting(true);
    try {
      let msg = null;
      if (t) {
        const r = await api.post(`/dm/${peer.peer_id}`, { text: t });
        msg = r.data;
      }
      if (file) {
        if (!msg) {
          const r = await api.post(`/dm/${peer.peer_id}`, { text: `📎 ${file.name}` });
          msg = r.data;
        }
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/dm/${msg.id}/file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    } finally {
      setPosting(false);
    }
  };

  const deleteMsg = async (m) => {
    if (m.author_user_id !== user.user_id) return;
    try {
      await api.delete(`/dm/message/${m.id}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>;
  }

  return (
    <div className="flex flex-col" style={{ height }} data-testid="direct-chat">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-emerald-50/40 rounded-lg" data-testid="dm-messages">
        {messages.length === 0 && (
          <div className="text-center py-10 text-sm text-slate-500 italic">
            Este es el inicio de tu conversación privada con {peer?.peer_name || "este usuario"}.
          </div>
        )}
        {messages.map((m, i) => {
          const isMe = m.author_user_id === user.user_id;
          const prev = messages[i - 1];
          const newDay = !prev || chatDayLabel(prev.created_at) !== chatDayLabel(m.created_at);
          const bubbleCls = isMe
            ? "bg-emerald-100 border-emerald-200 rounded-tr-none ml-auto"
            : "bg-white border-slate-200 rounded-tl-none";
          return (
            <div key={m.id}>
            {newDay && (
              <div className="flex justify-center my-3" data-testid={`dm-day-${m.id}`}>
                <span className="bg-white/90 border border-slate-200 text-slate-600 text-[11px] font-semibold px-3 py-1 rounded-full shadow-sm">
                  {chatDayLabel(m.created_at)}
                </span>
              </div>
            )}
            <div className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.id}`}>
              <div className={`max-w-[80%] border rounded-lg p-3 shadow-sm ${bubbleCls}`}>
                {!isMe && (
                  <div className="text-[10px] font-bold text-blue-700 mb-0.5">{m.author_name || "—"}</div>
                )}
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{m.text}</p>
                {m.attachments?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.attachments.map((f) => (
                      <div key={f.id} className="inline-flex items-center gap-1.5 bg-white/70 border border-slate-200 rounded-md px-2 py-1 text-[11px]">
                        <Paperclip className="h-3 w-3 text-slate-500" />
                        <span className="text-slate-800 truncate max-w-[160px]" title={f.original_filename}>{f.original_filename}</span>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/view`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">ver</a>
                      </div>
                    ))}
                  </div>
                )}
                <div className={`text-[10px] mt-1.5 flex items-center gap-2 ${isMe ? "justify-end text-emerald-700" : "text-slate-400"}`}>
                  <span title={crDateTime(m.created_at)} data-testid={`dm-time-${m.id}`}>{crTime(m.created_at)}</span>
                  {isMe && (
                    <button type="button" onClick={() => deleteMsg(m)} className="hover:text-rose-600" title="Eliminar" data-testid={`dm-del-${m.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-slate-200 bg-slate-50 p-2.5 mt-2 rounded-b-lg flex items-center gap-2">
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dm-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()} className="h-9 w-9 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-600" title="Adjuntar" data-testid="dm-file-btn">
          <Paperclip className="h-4 w-4" />
        </button>
        {file && (
          <span className="text-[11px] text-slate-600 truncate max-w-[140px] bg-slate-100 px-2 py-0.5 rounded">
            {file.name}
            <button className="ml-1 text-rose-600" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>✕</button>
          </span>
        )}
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Escribir a ${peer?.peer_name || "…"}`}
          className="flex-1 h-10 rounded-full px-4 border-slate-200"
          data-testid="dm-input"
        />
        <Button
          onClick={send}
          disabled={posting || (!text.trim() && !file)}
          className="h-10 w-10 rounded-full bg-emerald-600 hover:bg-emerald-700 p-0"
          data-testid="dm-send-btn"
        >
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
